import { DeleteTwoTone, EditTwoTone } from "@ant-design/icons";
import { Dropdown, Menu, message, Popconfirm, Table, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { deleteData, getData } from "../../../apiServices/common";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import { cglFormatDate, hasPermission } from "../../../utils/functions";
import ExtraContent from "../../Configuration/CommonComponents/ExtraContent";
import { getLocalShipmentConfig } from "./localShipmentConfig";

const LocalShipment = () => {
  // Router
  const location = useLocation();
  const navigate = useNavigate();
  const config = getLocalShipmentConfig(location?.pathname);

  // States
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Antd
  const { Title } = Typography;

  const getAllLocalShipments = useCallback(async () => {
    setLoading(true);
    const response = await getData(`${config.api}?page=${currentPage}`);
    setLoading(false);

    if (response && response?.data?.code === 200) {
      setMetaData({
        defaultPageSize: response?.data?.data?.per_page,
        current: response?.data?.data?.current_page,
        defaultCurrent: response?.data?.data?.current_page,
        total: response?.data?.data?.total,
      });
      setTableData(response?.data?.data?.data);
    } else {
      message.error(`Error getting Local ${config.label} data`);
    }
  }, [currentPage, config.api, config.label]);

  useEffect(() => {
    getAllLocalShipments();
  }, [refresh, getAllLocalShipments]);

  const deleteLocalShipment = async (id) => {
    setLoading(true);
    const response = await deleteData(`${config.api}/${id}`);
    setLoading(false);

    if (response && response?.code === 200) {
      message.success(`Local ${config.label} deleted successfully`);
      setRefresh((prev) => prev + 1);
    } else {
      message.error(`Error deleting Local ${config.label}`);
    }
  };

  const tableColumns = [
    {
      title: "Vehicle Number",
      dataIndex: "vehicle_number",
      key: "vehicle_number",
    },
    {
      title: "Type of Vehicle",
      dataIndex: "vehicle_type",
      key: "vehicle_type",
    },
    {
      title: "Vehicle Quantity",
      dataIndex: "vehicle_qty",
      key: "vehicle_qty",
    },
    {
      title: "Challan No.",
      dataIndex: ["challan_info", "challan_no"],
      key: "challan_no",
      render: (value) => value || "N/A",
    },
    {
      title: "Gate Pass No.",
      dataIndex: "gate_pass_no",
      key: "gate_pass_no",
      render: (value) => value || "N/A",
    },
    {
      title: "Raj Paribahan Type",
      dataIndex: ["transport_particular", "particular"],
      key: "transport_particular",
      render: (value) => value || "N/A",
    },
    {
      title: "Created By",
      dataIndex: ["creator", "name"],
      key: "creator",
      render: (value) => value || "N/A",
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => (value ? cglFormatDate(value) : "N/A"),
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => (
        <Dropdown.Button
          overlay={
            <Menu
              items={[
                {
                  label: (
                    <Popconfirm
                      title="Are you sure you want to delete?"
                      onConfirm={() => deleteLocalShipment(record?.id)}
                      okText="Yes"
                      cancelText="No"
                      disabled={!hasPermission([config.deletePermission])}
                    >
                      <span>
                        <DeleteTwoTone />
                      </span>
                      <span style={{ color: "red", marginLeft: 5 }}>
                        Delete
                      </span>
                    </Popconfirm>
                  ),
                  key: "1",
                  disabled: !hasPermission([config.deletePermission]),
                },
                {
                  label: (
                    <>
                      <span>
                        <EditTwoTone />
                      </span>
                      <span
                        type="button"
                        style={{ marginLeft: 5 }}
                        onClick={() =>
                          navigate(`${config.editPath}/${record?.id}`)
                        }
                      >
                        Edit
                      </span>
                    </>
                  ),
                  key: "2",
                  disabled: !hasPermission([config.editPermission]),
                },
              ]}
            />
          }
        />
      ),
    },
  ];

  return (
    <AppContent
      header={
        <AppPageHeader
          extra={
            <ExtraContent
              refresh={refresh}
              setRefresh={setRefresh}
              hideFilterButton
              addButtonText={
                hasPermission([config.createPermission])
                  ? "Add New"
                  : "No Permission"
              }
              disabled={!hasPermission([config.createPermission])}
              handleAddItemOpen={() => navigate(config.addPath)}
            />
          }
          title={<Title level={5}>Local {config.label}</Title>}
        />
      }
      content={
        <div className="table" style={{ marginTop: 10 }}>
          <Table
            loading={loading}
            dataSource={tableData}
            columns={tableColumns}
            rowKey="id"
            bordered
            pagination={{
              showSizeChanger: false,
              defaultPageSize: metaData?.defaultPageSize,
              current: metaData?.current,
              onChange: (newPage) => setCurrentPage(newPage),
              defaultCurrent: metaData?.defaultCurrent,
              total: metaData?.total,
            }}
          />
        </div>
      }
    />
  );
};

export default LocalShipment;
